'use client';
import React, { useEffect, useReducer } from 'react';
import { Button, Div, Text, TextField } from '@pezeshk-book/ui-kit';
import sendContactsMessageApi from '@api/contact-us/send-message';
import { useDispatch } from 'react-redux';
import { AlertActions } from '@store/alert/alert-action';

import validMobileNumber from '@utils/helpers/phone-validation';

const initialState = {
  loading: false,
  mobile: undefined,
  title: undefined,
  text: undefined,
  name: undefined,
  mobileError: false,
  titleError: false,
  textError: false,
  nameError: false,
  captchaToken: undefined,
  buttonEnabled: false,
};

const contactsReducer = (state: any, action: any) => {
  switch (action.type) {
    case 'SET_MOBILE':
      return {
        ...state,
        mobile: action?.payload.mobile,
      };

    case 'SET_NAME':
      return {
        ...state,
        name: action?.payload,
        nameError: !action?.payload,
      };

    case 'SET_TITLE':
      return {
        ...state,
        title: action?.payload,
        titleError: !action?.payload,
      };

    case 'SET_TEXT':
      return {
        ...state,
        text: action?.payload,
        textError: !action?.payload,
      };

    case 'SET_LOADING':
      return {
        ...state,
        loading: action?.payload,
      };

    case 'SET_ERROR':
      return {
        ...state,
        textError: !state.text,
        mobileError: !state.mobile,
        titleError: !state.title,
        nameError: !state.name,
        loading: false,
      };

    case 'SET_CAPTCHA_TOKEN':
      return {
        ...state,
        captchaToken: action?.payload,
      };

    case 'SET_BUTTON_ENABLED':
      return {
        ...state,
        buttonEnabled: action?.payload,
      };

    case 'SET_MOBILE_ERROR':
      return {
        ...state,
        mobileError: action?.payload.mobileError,
      };

    case 'SET_CLEAR':
      return {
        ...initialState,
      };

    default:
      return state;
  }
};

const ContactForm = ({ translate } : { translate: any}) => {
  const dispatch = useDispatch();
  const [contactsState, reducerDispatch] = useReducer(contactsReducer, initialState);

  useEffect(() => {
    const loadScriptByURL = (id: string, url: string, callback: any) => {
      const isScriptExist = document.getElementById(id);
      if (!isScriptExist) {
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.src = url;
        script.id = id;
        script.onload = function () {
          if (callback) {
            callback();
          }
        };
        document.body.appendChild(script);
      }

      if (isScriptExist && callback) {
        callback();
      }
    };
    loadScriptByURL('recaptcha-key', `https://www.google.com/recaptcha/api.js?render=${process.env.GOOGLE_RECAPTCHA_V3_SITE_KEY}`, function () {
    });
  }, []);

  const handleReCaptchaVerify = (e: any) => {
    // e.preventDefault();
    // @ts-ignore
    window?.grecaptcha.ready(() => {
      // @ts-ignore
      window?.grecaptcha.execute(process.env.GOOGLE_RECAPTCHA_V3_SITE_KEY, { action: 'submit' }).then((token: string) => {
        submitData(token);
      });
    });
  };

  const submitData = async (token: string) => {
    if (token) {
      reducerDispatch({ type: 'SET_LOADING', payload: true });
      if (!!contactsState.title && !!contactsState.name && !!contactsState.text && !!contactsState?.mobile && !contactsState.mobileError) {
        try {
          await sendContactsMessageApi({
            text: contactsState.text,
            title: contactsState.title,
            name: contactsState.name,
            mobile: contactsState.mobile,
            token: token,
          });
          dispatch(AlertActions.showAlert({
            text: translate.messages.submitted_successfully,
            severity: 'success',
          }));
          reducerDispatch({ type: 'SET_LOADING', payload: false });
          reducerDispatch({ type: 'SET_CLEAR' });
        } catch (error: any) {
          dispatch(AlertActions.showAlert({
            text: error?.statusText || translate.messages.network_error,
            severity: 'danger',
          }));
          reducerDispatch({ type: 'SET_LOADING', payload: false });
        }
      } else {
        dispatch(AlertActions.showAlert({
          text: translate.messages.fields_required,
          severity: 'warning',
        }));
        reducerDispatch({ type: 'SET_ERROR' });
      }
    } else {
      dispatch(AlertActions.showAlert({ severity: 'danger', text: translate.messages.login_required }));
    }
  };

  const setMobile = (e: any) => {
    const value = e.target.value;
    reducerDispatch({ type: 'SET_MOBILE', payload: { mobile: value } });
    if (value.length >= 10) {
      const validMobile = validMobileNumber(value);
      reducerDispatch({ type: 'SET_MOBILE_ERROR', payload: { mobileError: !validMobile.isValid } });
    } else if (!contactsState.mobileError) {
      reducerDispatch({ type: 'SET_MOBILE_ERROR', payload: { mobileError: true } });
    }
  };

  return (
    <div id={'submit'} className={'w-full'}>
      <Text align={'start'} typography={'xl'} type={'bold'}>{translate.title}</Text>
      <Text align={'start'} typography={'sm'}>{translate.sub_title}</Text>
      <Text align={'start'} typography={'sm'}>{translate.description}</Text>
      <Div className={'flex-col gap-4'}>
        <Div className={'flex-col sm:flex-row gap-4 mt-10 justify-between'}>
          <TextField
            className={'!w-full'}
            label={translate.text_fields.title}
            value={contactsState.title || ''}
            onChange={(e: any) => reducerDispatch({ type: 'SET_TITLE', payload: e.target.value })}
            helperText={contactsState.titleError ? translate.messages.title_required : undefined}
            error={contactsState.titleError}
          />
          <TextField
            className={'!w-full'}
            label={translate.text_fields.full_name}
            value={contactsState.name || ''}
            onChange={(e: any) => reducerDispatch({ type: 'SET_NAME', payload: e.target.value })}
            helperText={contactsState.nameError ? translate.messages.full_name_required : undefined}
            error={contactsState.nameError}
          />
          <TextField
            className={'!w-full'}
            label={translate.text_fields.phone_number}
            value={contactsState.mobile || ''}
            onChange={setMobile}
            helperText={!contactsState.mobile && contactsState.mobileError ? translate.messages.phone_number_required : contactsState.mobileError ? translate.messages.wrong_number_error : undefined}
            error={contactsState.mobileError}
          />
        </Div>
        <TextField
          inputClassName={'min-h-28'}
          size={'huge'}
          label={translate.text_fields.description}
          multiline={true}
          value={contactsState.text || ''}
          onChange={(e: any) => reducerDispatch({ type: 'SET_TEXT', payload: e.target.value })}
          helperText={contactsState.textError ? translate.messages.description_required : undefined}
          error={contactsState.textError}
        />
        <Div className={'w-full flex-col'}>
          <Div className={'w-full justify-end z-10'}>
            <Button loading={contactsState.loading} disabled={contactsState.loading} onClick={handleReCaptchaVerify} className={'w-full sm:w-44'}>
              {translate.button}
            </Button>
          </Div>
        </Div>
      </Div>
    </div>
  );
};
export default ContactForm;
